import { useBooleanState } from "@gettactic/helpers";
import { cn } from "@gettactic/helpers/src/css/cn";
import { mergeRefs } from "@gettactic/helpers/src/dom/mergeRef";
import React from "react";
import { FormControlWrapper } from "../FormControlWrapper";
import { FormControlOutline } from "../FormControlWrapper/FormControlOutline";
import { FormControlSlot } from "../FormControlWrapper/FormControlSlot";
import { useFormControlWrapper } from "../FormControlWrapper/FormControlWrapperProvider";
import {
  disabledCssClasses,
  disabledOutlineClasses
} from "../FormControlWrapper/defines";
import { splitFormControlWrapperProps } from "../FormControlWrapper/helpers";
import {
  TextInputProps,
  TextInputPropsWithFormControl
} from "./TextInput.types";
import { Button } from "@cmp-src/primitives/button";

export const TextInputInput = React.forwardRef<
  HTMLInputElement,
  TextInputProps
>((props, ref) => {
  const { inputClassName, className, type, disableOutline, ...rest } = props;
  const { id, setCharactersCounter, minLength, maxLength, isReadOnly } =
    useFormControlWrapper();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const isPasswordVisible = useBooleanState();

  React.useEffect(() => {
    const handleChange = (event: Event) => {
      if (event.target instanceof HTMLInputElement) {
        setCharactersCounter?.(event.target.value.length || 0);
      }
    };

    inputRef?.current?.addEventListener("onChange", handleChange);

    return () => {
      inputRef?.current?.removeEventListener("onChange", handleChange);
    };
  }, [inputRef.current]);

  return (
    <>
      <input
        {...rest}
        className={cn(
          "flex h-8 w-full border-0 bg-transparent text-sm shadow-none outline-none ring-0 transition-colors placeholder:text-muted-foreground",
          disabledCssClasses,
          disabledOutlineClasses,
          inputClassName,
          className
        )}
        style={{
          borderWidth: 0,
          outline: 0,
          border: 0,
          boxShadow: "none",
          ...rest.style
        }}
        maxLength={maxLength}
        minLength={minLength}
        ref={mergeRefs([ref, inputRef])}
        type={
          type === "password"
            ? isPasswordVisible.state
              ? "text"
              : "password"
            : type
        }
        id={id}
        readOnly={isReadOnly || rest.readOnly}
        tabIndex={isReadOnly ? -1 : rest.tabIndex}
      />
      {type === "password" && (
        <FormControlSlot>
          <Button
            type={"button"}
            size={"sm"}
            //icon={isPasswordVisible.state ? "Eye" : "EyeOff"}
            onClick={() => isPasswordVisible?.toggle()}
          />
        </FormControlSlot>
      )}
    </>
  );
});

TextInputInput.displayName = "TextInputInput";

export const TextInput = React.forwardRef<
  HTMLInputElement,
  TextInputPropsWithFormControl
>((props, ref) => {
  const [wrapperProps, inputProps] = splitFormControlWrapperProps(props);
  const isDisabled = wrapperProps.isDisabled || inputProps.disabled;
  const isReadOnly = wrapperProps.isReadOnly || inputProps.readOnly;

  return (
    <FormControlWrapper
      enableSlotsInputFocus
      {...wrapperProps}
      wrapperClassName={cn("shadow-input", wrapperProps.wrapperClassName)}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
    >
      <TextInputInput ref={ref} {...inputProps} disabled={isDisabled} />
      {inputProps.disableOutline !== true && <FormControlOutline />}
    </FormControlWrapper>
  );
});

TextInput.displayName = "TextInput";
